import type { MaybeRefOrGetter } from "vue";
import { onScopeDispose, ref, toValue, watch } from "vue";

export const SCROLL_SETTLE_MS = 150;

export interface ScrollingOptions {
  active: MaybeRefOrGetter<boolean>;
  /** Scrolls inside this element are not counted. */
  ignoreWithin?: MaybeRefOrGetter<HTMLElement | null | undefined>;
}

/**
 * Whether anything on the page scrolled in the last {@link SCROLL_SETTLE_MS} ms, while `active`.
 * Any scroller counts, not only the ones holding a given element: a grid scrolls its header from
 * the body's scroll handler, and the header's own scroll event only comes a frame later.
 */
export function useScrolling(options: ScrollingOptions) {
  const scrolling = ref(false);
  let settleTimeout: ReturnType<typeof setTimeout> | undefined;

  function onScroll(event: Event) {
    if (event.target instanceof Node && toValue(options.ignoreWithin)?.contains(event.target)) return;
    scrolling.value = true;
    clearTimeout(settleTimeout);
    settleTimeout = setTimeout(() => (scrolling.value = false), SCROLL_SETTLE_MS);
  }

  function stop() {
    document.removeEventListener("scroll", onScroll, { capture: true });
    clearTimeout(settleTimeout);
    scrolling.value = false;
  }

  watch(
    () => toValue(options.active),
    (active) => {
      if (active) document.addEventListener("scroll", onScroll, { capture: true, passive: true });
      else stop();
    },
    { immediate: true },
  );

  onScopeDispose(stop);

  return { scrolling };
}
